import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsageDataByAdvisorIdRequest } from '@/model/usageAnalyticsModel';
import { AppState } from '@/model/store';
import { aggregateMetrics, createEmptyMetrics } from '@shared/config/usageMetricsConfig';

export const useAdvisorUsageAnalytics = (advisorId: string | undefined) => {
  const dispatch = useDispatch();
  const usageAnalytics = useSelector((state: AppState) => state.usageAnalytics);

  useEffect(() => {
    if (advisorId) {
      dispatch(fetchUsageDataByAdvisorIdRequest({ advisorId }));
    }
  }, [advisorId, dispatch]);

  const calculatedMetrics = useMemo(() => {
    if (!usageAnalytics.data?.advisorUsageData) {
      return createEmptyMetrics();
    }
    const advisorsList = Object.values(usageAnalytics.data.advisorUsageData) as AdvisorUsageData[];
    return aggregateMetrics(advisorsList.map(a => a.usageMetrics));
  }, [usageAnalytics.data?.advisorUsageData]);

  const advisorsList = useMemo((): AdvisorUsageData[] => {
    if (!usageAnalytics.data?.advisorUsageData) {
      return [];
    }
    return Object.values(usageAnalytics.data.advisorUsageData);
  }, [usageAnalytics.data?.advisorUsageData]);

  const activeAdvisorsCount = useMemo(() => {
    return advisorsList.filter(a => a.usageMetrics.totalAdvisorLogins > 0).length;
  }, [advisorsList]);

  return {
    usageAnalytics,
    calculatedMetrics,
    advisorsList,
    activeAdvisorsCount,
  };
};
